import React, { useEffect, useState } from "react";
import { Wallet, Gift, CreditCard, BadgeCheck, CalendarClock } from "lucide-react";
import { useBooking } from "../../context/BookingContext";
import { Button } from "../ui/button";
import api from "../../lib/api";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (d) => {
  if (!d) return null;
  try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
};

export function WalletTab() {
  const { openBooking } = useBooking();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/member/wallet").then((r) => setData(r.data)).catch(() => setData({ available: false, matched: false, store_credit: 0, gift_cards: [], memberships: [] }));
  }, []);

  if (data === null) return <p className="text-[#8A9492]" data-testid="wallet-loading">Loading your wallet…</p>;

  const { available, matched, store_credit = 0, gift_cards = [], memberships = [] } = data;
  const giftTotal = gift_cards.reduce((s, g) => s + (g.balance || 0), 0);
  const hasAnything = store_credit > 0 || gift_cards.length > 0 || memberships.length > 0;

  return (
    <div data-testid="wallet-tab">
      <h2 className="font-heading text-2xl font-light text-ink mb-6">My wallet</h2>

      {!available && (
        <div className="bg-white rounded-2xl p-10 border border-slate-100 text-center" data-testid="wallet-unavailable">
          <Wallet className="h-8 w-8 text-[#c9c4dc] mx-auto" />
          <p className="text-[#8A9492] mt-3">Your wallet is temporarily unavailable. Please try again shortly.</p>
        </div>
      )}

      {available && !matched && (
        <div className="bg-white rounded-2xl p-10 border border-slate-100 text-center" data-testid="wallet-nomatch">
          <Wallet className="h-8 w-8 text-[#c9c4dc] mx-auto" />
          <p className="text-ink font-medium mt-3">We couldn't find a wallet linked to your email.</p>
          <p className="text-[#8A9492] text-sm mt-1 max-w-md mx-auto">
            Credit, gift cards and membership perks appear here once you've visited or purchased with the email on your account.
          </p>
        </div>
      )}

      {available && matched && (
        <div className="space-y-6">
          {/* balance summary */}
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="iri-gradient rounded-2xl p-7" data-testid="wallet-store-credit">
              <CreditCard className="h-6 w-6 text-ink/70" strokeWidth={1.5} />
              <p className="text-ink/70 text-sm mt-4">Store credit</p>
              <p className="font-heading text-5xl font-light text-ink mt-1" data-testid="wallet-store-credit-value">{money(store_credit)}</p>
            </div>
            <div className="bg-white rounded-2xl p-7 border border-slate-100" data-testid="wallet-giftcard-total">
              <Gift className="h-6 w-6 text-iri-mid" strokeWidth={1.5} />
              <p className="text-[#8A9492] text-sm mt-4">Gift card balance</p>
              <p className="font-heading text-5xl font-light text-ink mt-1">{money(giftTotal)}</p>
              <p className="text-xs text-[#8A9492] mt-1">{gift_cards.length} card{gift_cards.length === 1 ? "" : "s"}</p>
            </div>
          </div>

          {!hasAnything && (
            <div className="bg-white rounded-2xl p-8 border border-slate-100 text-center" data-testid="wallet-empty">
              <p className="text-[#8A9492]">No credit or perks on your account yet.</p>
            </div>
          )}

          {/* gift cards */}
          {gift_cards.length > 0 && (
            <section data-testid="wallet-giftcards">
              <h3 className="text-xs uppercase tracking-[0.2em] text-[#8A9492] mb-3">Gift cards</h3>
              <div className="space-y-3">
                {gift_cards.map((g, i) => (
                  <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 flex items-center justify-between" data-testid={`wallet-giftcard-${i}`}>
                    <div className="flex items-center gap-3">
                      <Gift className="h-5 w-5 text-iri-mid" />
                      <span className="text-[#5C6664] text-sm">Gift card {g.code}</span>
                    </div>
                    <span className="font-heading text-lg text-ink">{money(g.balance)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* memberships + credits */}
          {memberships.length > 0 && (
            <section data-testid="wallet-memberships">
              <h3 className="text-xs uppercase tracking-[0.2em] text-[#8A9492] mb-3">Memberships & credits</h3>
              <div className="space-y-4">
                {memberships.map((m, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100" data-testid={`wallet-membership-${i}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <BadgeCheck className="h-5 w-5 text-iri-mid" />
                        <h4 className="font-heading text-lg text-ink">{m.name}</h4>
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full ${m.status === "ACTIVE" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {(m.status || "").replace("_", " ").toLowerCase()}
                      </span>
                    </div>
                    {m.credits?.length > 0 && (
                      <ul className="mt-3 space-y-1.5">
                        {m.credits.map((c, j) => (
                          <li key={j} className="text-sm text-[#5C6664] flex items-center gap-2">
                            <span className="iri-gradient text-ink text-xs font-medium px-2 py-0.5 rounded-full">{c.quantity}×</span>
                            {c.services.join(", ") || "Service credit"}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 text-xs text-[#8A9492]">
                      {m.next_charge ? (
                        <span className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Renews {fmtDate(m.next_charge)}</span>
                      ) : <span />}
                      {m.unit_price != null && <span>{money(m.unit_price)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {(store_credit > 0 || giftTotal > 0) && (
            <div className="text-center">
              <Button onClick={() => openBooking()} className="iri-gradient text-ink rounded-full font-medium" data-testid="wallet-book-button">
                Book & use your balance
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
